import { Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { fromEvent, merge, Observable } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { findClosest, findClosestElement } from '../../utils';
import { FiBrowserEventsService } from '../../fi-shared-services/fi-browser-events.service';
import { isNullOrUndefined } from '@fi-app/shared/utils/is-null-or-undefined';

@Injectable()
export class FiTextPanelHelperService {
    private renderer: Renderer2;

    constructor(
        private browserEventsService: FiBrowserEventsService,
        private rendererFactory: RendererFactory2,
    ) {
        this.renderer = rendererFactory.createRenderer(null, null);
    }

    subscribeToTextPanelVisibilityChangeOnClick(
        editableWrapperElement: HTMLElement | (() => HTMLElement),
        ngOnDestroy$: Observable<void>,
        callbackFn: (isPanelVisible: boolean) => void,
        handleClickInDialogFn?: (target: HTMLElement) => boolean,
    ) {
        this.browserEventsService
            .getDocumentEventSubject$('click')
            .pipe(takeUntil(ngOnDestroy$))
            .subscribe(($event: MouseEvent) => {
                const element: HTMLElement =
                    typeof editableWrapperElement === 'function' ? editableWrapperElement() : editableWrapperElement;

                if (!($event.target instanceof Element)) return;

                const target: HTMLElement = $event.target as HTMLElement;
                if (findClosestElement(target, element)) {
                    // click on editable element => show panel
                    callbackFn(true);
                } else if (findClosest(target, '.fi-simple-text-panel')) {
                    return callbackFn(true);
                } else if (!!findClosest(target, '.mat-mdc-menu-panel')) {
                    // click on opened menus, dialogs etc, just ignore
                } else if (findClosest(target, '.mat-mdc-dialog-panel')) {
                    // click inside dialog, if provided callback, call it
                    if (handleClickInDialogFn) {
                        return callbackFn(handleClickInDialogFn(target));
                    }
                    // if not, just ignore
                } else if (findClosest(target, '.fi-text-editor-tag-users-autocomplete-panel')) {
                    callbackFn(true);
                }
                // check window selection, if selected text is inside editable element, show panel
                else if (this.isTextSelectedInElement(element)) {
                    callbackFn(true);
                } else {
                    // click on other place, close panel
                    callbackFn(false);
                }
            });
    }

    private isTextSelectedInElement(element: HTMLElement): boolean {
        const selection: Selection = window.getSelection();
        if (selection.rangeCount === 0) return false;
        const range: Range = selection.getRangeAt(0);

        if (range.startContainer === range.endContainer && range.endOffset === range.startOffset) {
            return false;
        }

        return (
            !isNullOrUndefined(findClosestElement(range.startContainer, element)) ||
            !isNullOrUndefined(findClosestElement(range.endContainer, element))
        );
    }

    subscribeToScrollUpdating(
        editableWrapperElement: HTMLElement,
        panelElement: HTMLElement,
        topOffset: number,
        ngOnDestroy$: Observable<void>,
    ) {
        const updatePanelPosition = () =>
            this.updatePanelTopToStayVisible(editableWrapperElement, panelElement, topOffset);

        merge(
            this.browserEventsService.getWindowEventSubject$('scroll'),
            fromEvent(document, 'scroll', { capture: true, passive: true }),
        )
            .pipe(takeUntil(ngOnDestroy$), debounceTime(20))
            .subscribe(updatePanelPosition);

        const visibilityObserver = new MutationObserver(updatePanelPosition);
        visibilityObserver.observe(panelElement, { attributes: true, attributeFilter: ['hidden'] });
        ngOnDestroy$.subscribe(() => visibilityObserver.disconnect());

        updatePanelPosition();
    }

    private updatePanelTopToStayVisible(
        editableWrapperElement: HTMLElement,
        panelElement: HTMLElement,
        topOffset: number,
    ): void {
        if (!panelElement.isConnected || !editableWrapperElement.isConnected || panelElement.offsetParent === null) {
            return;
        }

        const minVisibleTop = Math.max(topOffset, this.getMinVisibleTop());
        const offsetParent = (panelElement.offsetParent as HTMLElement) ?? editableWrapperElement;
        const parentTop = offsetParent.getBoundingClientRect().top;
        const defaultTop = this.getDefaultCssTop(panelElement);
        const naturalViewportTop = parentTop + defaultTop;

        if (naturalViewportTop < minVisibleTop) {
            this.renderer.setStyle(panelElement, 'top', `${minVisibleTop - parentTop}px`);
        } else {
            this.renderer.removeStyle(panelElement, 'top');
        }
    }

    private getDefaultCssTop(panelElement: HTMLElement): number {
        const storedDefaultTop = panelElement.dataset.fiTextPanelDefaultTop;
        if (!isNullOrUndefined(storedDefaultTop)) {
            return Number(storedDefaultTop);
        }

        const inlineTop = panelElement.style.top;
        panelElement.style.top = '';
        const defaultTop = parseFloat(getComputedStyle(panelElement).top) || 0;
        panelElement.style.top = inlineTop;
        panelElement.dataset.fiTextPanelDefaultTop = String(defaultTop);
        return defaultTop;
    }

    private getMinVisibleTop(): number {
        const occupyingTopElements = document.querySelectorAll('fi-main-toolbar, fi-page-header');
        let minVisibleTop = 0;

        occupyingTopElements.forEach((element: Element) => {
            if (!(element instanceof HTMLElement) || element.hidden) {
                return;
            }

            const style = getComputedStyle(element);
            if (style.display === 'none' || style.visibility === 'hidden') {
                return;
            }

            const rect = element.getBoundingClientRect();
            if (rect.height <= 0 || rect.top > minVisibleTop + 1 || rect.bottom <= minVisibleTop) {
                return;
            }

            minVisibleTop = Math.max(minVisibleTop, rect.bottom);
        });

        return minVisibleTop;
    }

    insertTag(contentEditableElem: HTMLElement): void {
        if (isNullOrUndefined(contentEditableElem)) {
            return;
        }

        const selection = window.getSelection();
        const range = selection.getRangeAt(0);

        // Check if selection is within contentEditableElem
        if (!contentEditableElem.contains(range.commonAncestorContainer)) {
            // If selection is outside, create new range at end of contentEditableElem
            range.selectNodeContents(contentEditableElem);
            range.collapse(false);
        }

        range.deleteContents();
        const textNode = document.createTextNode('@');
        range.insertNode(textNode);
        range.setStartAfter(textNode);
        range.setEndAfter(textNode);
        selection.removeAllRanges();
        selection.addRange(range);
        contentEditableElem.focus();
    }
}
